import {inject, Injectable} from '@angular/core';
import {VaultsService} from '../../api/services/vaults.service';
import {UserGroupsService} from '../../api/services/user-groups.service';
import {UserGroupStore} from '../../stores/user-group.store';
import {UserGroupMembersService} from '../../api/services/user-group-members.service';
import {DialogService} from '../dialog/dialog.service';
import {SnackbarService} from '../snackbar/snackbar.service';
import {TranslationKey} from '../../types/available-translations.type';
import {VaultQuery} from '../../queries/vault.query';
import {UserGroupQuery} from '../../queries/user-group.query';
import {PermissionService} from '../permission/permission.service';
import {UserGroup} from '../../api/models/user-group';
import {HttpErrorResponse} from '@angular/common/http';
import {firstValueFrom, lastValueFrom} from 'rxjs';
import {AuthService} from '../auth/auth.service';

@Injectable({
    providedIn: 'root'
})
export class UserGroupService {
    private readonly apiVaultService = inject(VaultsService);
    private readonly apiUserGroupService = inject(UserGroupsService);
    private readonly apiUserGroupMemberService = inject(UserGroupMembersService);
    private readonly authService = inject(AuthService);
    private readonly vaultQuery = inject(VaultQuery);
    private readonly userGroupQuery = inject(UserGroupQuery);
    private readonly userGroupStore = inject(UserGroupStore);
    private readonly dialogService = inject(DialogService);
    private readonly snackbarService = inject(SnackbarService);
    private readonly permissionService = inject(PermissionService);

    public async fetchUserGroups(vaultId: string): Promise<boolean> {
        this.userGroupStore.setLoading(true);
        let status = false;

        try {
            const groups = await lastValueFrom(this.apiVaultService.VaultsGetUserGroups({
                Authorization: await this.authService.getBearer(),
                vaultId
            }));
            const activeGroups = groups.filter(group => group.active);
            this.userGroupStore.set(activeGroups);
            status = true;
        } catch (e) {
            console.error(e);
        } finally {
            this.userGroupStore.setLoading(false);
        }

        return status;
    }

    public async fetchUserGroupMembers(userGroupId: string): Promise<boolean> {
        this.userGroupStore.setLoading(true);
        let status = false;

        try {
            const member = await firstValueFrom(this.apiUserGroupService.UserGroupsGetMembers({
                Authorization: await this.authService.getBearer(),
                userGroupId
            }));
            this.userGroupStore.update({ member });
            status = true;
        } catch (e) {
            console.error(e);
        } finally {
            this.userGroupStore.setLoading(false);
        }

        return status;
    }

    public async fetchUnassignedUserGroupMembers(vaultId: string): Promise<boolean> {
        this.userGroupStore.setLoading(true);
        let status = false;

        try {
            const member = await firstValueFrom(this.apiVaultService.VaultsGetUserGroupUnassignedMembers({
                Authorization: await this.authService.getBearer(),
                vaultId
            }));
            this.userGroupStore.update({ member });
            status = true;
        } catch (e) {
            console.error(e);
        } finally {
            this.userGroupStore.setLoading(false);
        }

        return status;
    }

    public setActiveGroup(ids: Array<string>): void {
        this.userGroupStore.setActive(ids);
        if (ids.at(0) && ids.at(0) !== 'undefined') {
            this.permissionService.fetchUserGroupPermissions(ids[0])
                .then();
        }
    }

    public async addUserToUnassignedVaultUserGroup(userId: string): Promise<void> {
        const currentVaultId = this.vaultQuery.getFirstActiveVaultId();

        if (!currentVaultId) {
            throw new Error('Could not retrieve current vaultId to determine if given user is a member of the current vault.');
        }

        const vaultMembers = await firstValueFrom(this.apiVaultService.VaultsGetMembers({
            Authorization: await this.authService.getBearer(),
            vaultId: currentVaultId,
        }));

        const isMemberOfCurrentVault = vaultMembers.some(vaultMember => vaultMember.userId === userId);

        if (isMemberOfCurrentVault) {
            const userGroups = await lastValueFrom(this.apiVaultService.VaultsGetUserGroups({
                Authorization: await this.authService.getBearer(),
                vaultId: currentVaultId
            }));

            const activeUserGroupsWithMembers = userGroups
                .filter(userGroup => userGroup.active && userGroup.memberCount > 0);

            for (const userGroup of activeUserGroupsWithMembers) {
                const userGroupMembers = await firstValueFrom(this.apiUserGroupService.UserGroupsGetMembers({
                    Authorization: await this.authService.getBearer(),
                    userGroupId: userGroup.id,
                }));

                const userGroupMember = userGroupMembers
                    .find(member => member.userId === userId);

                if (userGroupMember) {
                    await firstValueFrom(this.apiUserGroupMemberService.UserGroupMembersDelete({
                        Authorization: await this.authService.getBearer(),
                        memberId: userGroupMember.id,
                    }));
                }
            }
        } else {
            await firstValueFrom(this.apiVaultService.VaultsAddMember({
                Authorization: await this.authService.getBearer(),
                vaultId: currentVaultId,
                creationData: {
                    userId
                }
            }));
        }
    }

    public async addUserToVaultUserGroup(userGroupId: string, userId: string): Promise<void> {
        const userGroup: UserGroup | undefined = this.userGroupQuery.getUserGroupById(userGroupId);

        if (!userGroup) {
            throw new Error('Could not retrieve user group for given userGroupId.');
        }

        await firstValueFrom(this.apiUserGroupService.UserGroupsAddMember({
            Authorization: await this.authService.getBearer(),
            userGroupId: userGroup.id,
            data: {
                userId,
            }
        }));
    }

    public async removeMemberFromUserGroup(memberId: string): Promise<boolean> {
        this.userGroupStore.setLoading(true);
        let isDeletedSuccessfully = false;
        try {
            await firstValueFrom(this.apiUserGroupMemberService.UserGroupMembersDelete({
                Authorization: await this.authService.getBearer(),
                memberId,
            }));
            isDeletedSuccessfully = true;
        } catch (error: unknown) {
            const httpError = error as HttpErrorResponse;
            if ('code' in httpError.error) {
                this.dialogService.showDialog({
                    messageTranslationKey: 'ERROR.CODES.CODE_' + httpError.error.code as TranslationKey
                }, 'error-remove-member-from-user-group');
            } else {
                const members = this.userGroupStore.getValue().member;
                const selectedMember = members.find(member => member.id === memberId);
                if (selectedMember) {
                    this.snackbarService.showError('ERROR.REMOVE_MEMBER_FROM_GROUP', error as Error, { user: selectedMember?.userFullName });
                } else {
                    this.snackbarService.showGenericError(error as Error);
                }
            }
        } finally {
            this.userGroupStore.setLoading(false);
        }
        return isDeletedSuccessfully;
    }
}
